import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import ImageCropDialog from '../ImageCropDialog';

const FileUploadWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px dashed #d1d5db;
  border-radius: 6px;
  background-color: #ffffff;
  color: #6b7280;
  font-size: 16px;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    border-color: #3498db;
    background-color: #f8fafc;
  }
  
  &:disabled {
    background-color: #f9f9f9;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const FileInfo = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f3f4f6;
  border-radius: 4px;
  font-size: 14px;
  color: #374151;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
  padding: 4px;
  
  &:hover {
    color: #c0392b;
  }
`;

const CropButton = styled.button`
  background: none;
  border: none;
  color: #3498db;
  cursor: pointer;
  font-size: 12px;
  padding: 4px;
  margin-right: 8px;
  
  &:hover {
    color: #2980b9;
  }
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 4px;
  margin-top: 8px;
  border: 1px solid #d1d5db;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
`;

interface FileUploadProps {
  value: File | null;
  onChange: (value: File | null) => void;
  required?: boolean;
  disabled?: boolean;
  accept?: string;
  maxSize?: number; // in bytes
  enableCrop?: boolean;
  name?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  value,
  onChange,
  required = false,
  disabled = false,
  accept = '*/*',
  maxSize = 5 * 1024 * 1024, // 5MB default
  enableCrop = false,
  name = ''
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const isImageFile = (file: File) => {
    return file.type.startsWith('image/');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > maxSize) {
        alert(`File size must be less than ${maxSize / (1024 * 1024)}MB`);
        return;
      }
      
      if (enableCrop && isImageFile(file)) {
        setOriginalFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageUrl = e.target?.result as string;
          setImagePreviewUrl(imageUrl);
          setShowCropDialog(true);
        };
        reader.readAsDataURL(file);
      } else {
        onChange(file);
        if (isImageFile(file)) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setImagePreviewUrl(e.target?.result as string);
          };
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setOriginalFile(null);
    setImagePreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropComplete = (croppedImageBlob: Blob) => {
    console.log('Crop completed - storing image locally, no API call');
    const croppedFile = new File([croppedImageBlob], originalFile?.name || 'cropped-image.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    onChange(croppedFile);
    
    // Create preview URL for the cropped image
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(croppedFile);
    
    setShowCropDialog(false);
    // Reset file input so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropCancel = () => {
    setShowCropDialog(false);
    setOriginalFile(null);
    setImagePreviewUrl('');
    // Reset file input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropAgain = () => {
    if (originalFile) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setImagePreviewUrl(imageUrl);
        setShowCropDialog(true);
      };
      reader.readAsDataURL(originalFile);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <FileUploadWrapper>
      <FileInput
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept={accept}
        required={required}
        disabled={disabled}
        name={name}
      />
      
      <FileUploadButton
        type="button"
        onClick={handleButtonClick}
        disabled={disabled}
      >
        {value ? 'Change File' : 'Choose File'}
      </FileUploadButton>

      {value && (
        <FileInfo>
          <span>{value.name} ({formatFileSize(value.size)})</span>
          <ButtonGroup>
            {enableCrop && isImageFile(value) && originalFile && (
              <CropButton type="button" onClick={handleCropAgain}>
                Crop Again
              </CropButton>
            )}
            <RemoveButton type="button" onClick={handleRemove}>
              Remove
            </RemoveButton>
          </ButtonGroup>
        </FileInfo>
      )}

      {imagePreviewUrl && (
        <ImagePreview src={imagePreviewUrl} alt="Preview" />
      )}

      <ImageCropDialog
        isOpen={showCropDialog}
        onClose={handleCropCancel}
        imageSrc={imagePreviewUrl}
        onCropComplete={handleCropComplete}
        aspectRatio={6 / 4}
      />
    </FileUploadWrapper>
  );
};

export default FileUpload;